import type {
  AgentAnnotationEmphasis,
  AgentAnnotationShape,
} from "@/lib/agent-annotation";
import {
  nibSizeFor,
  sketchMarkStrokes,
  type SketchStroke,
} from "@/lib/sketch-stroke/sketch-mark";
import { SKETCH_INK_OVERFLOW_PX } from "@/lib/sketch-stroke/ink-style";

/** Loops drift outward by up to ~11.4 % of their radius: 7 % growth plus the
 * 4 % wobble, widened slightly by their rotation. */
const LOOP_DRIFT_RATIO = 0.12;

/** The fields of a mark that decide its drawn ink, in global coordinates. */
export interface AnnotationSketchInput {
  shape: AgentAnnotationShape;
  globalX: number;
  globalY: number;
  width: number;
  height: number;
  padding?: number;
  /** Target-local normalized points for path shapes. */
  path?: ReadonlyArray<{ x: number; y: number }>;
  arrowEnd?: boolean;
  emphasis?: AgentAnnotationEmphasis;
  strokeWidth?: number;
  annotationId?: string;
  seq?: number;
}

interface AnnotationContentBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** The padded box a mark is drawn in; squares and circles use the longer side. */
export function annotationContentBox(
  target: Pick<
    AnnotationSketchInput,
    "shape" | "globalX" | "globalY" | "width" | "height" | "padding"
  >,
): AnnotationContentBox {
  const padding = Math.max(0, target.padding ?? 0);
  let width = Math.max(1, target.width + padding * 2);
  let height = Math.max(1, target.height + padding * 2);
  if (target.shape === "square" || target.shape === "circle") {
    const side = Math.max(width, height);
    width = side;
    height = side;
  }
  return {
    left: target.globalX + target.width / 2 - width / 2,
    top: target.globalY + target.height / 2 - height / 2,
    width,
    height,
  };
}

/** Every display window and every re-render draws the same wobble. */
export function annotationSketchSeed(
  target: Pick<AnnotationSketchInput, "annotationId" | "seq">,
): string {
  return target.annotationId ?? `seq:${target.seq ?? 0}`;
}

/** The pen strokes of one mark, relative to its global content box. The
 * renderer, the pen tip and the pen start all read this one source. */
export function annotationSketchStrokes(target: AnnotationSketchInput): {
  box: AnnotationContentBox;
  strokes: SketchStroke[];
} {
  const box = annotationContentBox(target);
  const offsetX = target.globalX - box.left;
  const offsetY = target.globalY - box.top;
  const strokes = sketchMarkStrokes({
    shape: target.shape,
    width: box.width,
    height: box.height,
    path: target.path?.map((point) => ({
      x: offsetX + point.x * target.width,
      y: offsetY + point.y * target.height,
    })),
    arrowEnd: target.arrowEnd,
    nibSize: nibSizeFor(target.emphasis, target.strokeWidth),
    seed: annotationSketchSeed(target),
  });
  return { box, strokes };
}

/** How far a mark's ink may reach beyond its content box. Loops scale with
 * their size; every mark keeps at least the box wobble allowance. */
export function annotationInkOverflow(
  target: Pick<
    AnnotationSketchInput,
    | "shape"
    | "globalX"
    | "globalY"
    | "width"
    | "height"
    | "padding"
    | "emphasis"
    | "strokeWidth"
  >,
): number {
  if (target.shape !== "circle" && target.shape !== "ellipse") {
    return SKETCH_INK_OVERFLOW_PX;
  }
  const box = annotationContentBox(target);
  const radius = Math.max(box.width, box.height) / 2;
  return Math.max(
    SKETCH_INK_OVERFLOW_PX,
    Math.ceil(
      radius * LOOP_DRIFT_RATIO +
        nibSizeFor(target.emphasis, target.strokeWidth) / 2 +
        1,
    ),
  );
}
